import { useEffect, RefObject } from "react";

/**
 * Finds the first scrollable parent element
 */
const findScrollableParent = (
  element: HTMLElement | null
): HTMLElement | null => {
  if (!element) return null;

  let parent = element.parentElement;

  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    const isScrollable = /(auto|scroll)/.test(overflow + overflowY);
    const hasScrollableContent = parent.scrollHeight > parent.clientHeight;

    if (isScrollable && hasScrollableContent) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

/**
 * Prevents number input value changes on scroll while allowing modal/page scrolling.
 * When scrolling over a number input, it prevents the value from changing but
 * manually scrolls the nearest scrollable parent (modal/page) instead.
 *
 * @param inputRef - React ref to the input element
 * @param enabled - Whether to enable the scroll prevention (default: true)
 */
export const usePreventNumberInputScroll = (
  inputRef: RefObject<HTMLInputElement>,
  enabled: boolean = true
) => {
  useEffect(() => {
    if (!enabled) return;

    const input = inputRef.current;
    if (!input) return;

    const handleWheel = (e: WheelEvent) => {
      // Prevent the default behavior (number value change)
      e.preventDefault();

      // Find the scrollable parent and manually scroll it
      const scrollableParent = findScrollableParent(input);
      if (scrollableParent) {
        scrollableParent.scrollBy({
          top: e.deltaY,
          behavior: "auto",
        });
      } else {
        // Fallback to window scroll
        window.scrollBy({
          top: e.deltaY,
          behavior: "auto",
        });
      }
    };

    // Must use passive: false because we're calling preventDefault
    input.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      input.removeEventListener("wheel", handleWheel);
    };
  }, [inputRef, enabled]);
};
